import { LinkIcon } from '@approvalmax/ui';
import { ColumnDefinition, Flex, Table } from '@approvalmax/ui/src/components';
import { intl, typeGuardHelpers } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { DoneIcon } from 'modules/sprites';
import { isFieldVisible } from 'shared/helpers';
import { NetSuiteMatchingLine } from 'shared/hooks/(matchings)';

import { isLineMatchingDisabled } from './NetSuiteMatchingPopup.helpers';
import { messages } from './NetSuiteMatchingPopup.messages';
import { GetColumnDefinitionsParams, NetSuiteSourceLine } from './NetSuiteMatchingPopup.types';

export const getColumnDefinitions = ({
    lineType,
    fieldSettings,
    sourceLine,
    customFieldsColumnDefinitions,
}: GetColumnDefinitionsParams) => {
    const columnDefinitions: ColumnDefinition<NetSuiteSourceLine | NetSuiteMatchingLine>[] = [];

    if (lineType === domain.NetSuiteMatchingLineType.Expense) {
        columnDefinitions.push(
            {
                id: 'category',
                name: messages.categoryColumnName,
                value: (li) => li.category?.text,
                minWidth: 160,
            },
            {
                id: 'account',
                name: messages.accountColumnName,
                value: (li) => li.account?.text,
                minWidth: 150,
            }
        );
    }

    if (lineType === domain.NetSuiteMatchingLineType.Item) {
        columnDefinitions.push(
            {
                id: 'item',
                name: messages.itemColumnName,
                value: (li) => li.item?.text,
                minWidth: 100,
            },
            {
                id: 'description',
                name: messages.descriptionColumnName,
                value: (li) => li.description,
                minWidth: 210,
            },
            {
                id: 'billed',
                textAlign: 'right',
                headerTextAlign: 'right',
                name: messages.billedColumnName,
                value: (li) => ('billed' in li ? intl.formatNumber(li.billed, 'auto') : null),
                minWidth: 40,
            },
            {
                id: 'quantity',
                textAlign: 'right',
                headerTextAlign: 'right',
                name: messages.quantityColumnName,
                value: (li) => intl.formatNumber(li.quantity, 'auto'),
                minWidth: 40,
            },
            {
                id: 'units',
                textAlign: 'right',
                headerTextAlign: 'right',
                name: messages.unitsColumnName,
                value: (li) => ('units' in li ? li.units?.text : null),
                minWidth: 40,
            },
            {
                id: 'unitPrice',
                textAlign: 'right',
                headerTextAlign: 'right',
                name: messages.unitPriceColumnName,
                value: (li) => intl.formatNumber(li.unitPrice, 'auto'),
                minWidth: 100,
            }
        );
    }

    columnDefinitions.push(
        {
            id: 'amount',
            name: messages.amountColumnName,
            value: (li) => intl.formatNumber(li.amount, 'auto'),
            minWidth: 100,
        },
        {
            id: 'taxCode',
            textAlign: 'right',
            headerTextAlign: 'right',
            name: messages.taxCodeColumnName,
            value: (li) => li.taxCode?.text,
            minWidth: 80,
        },
        {
            id: 'taxRate',
            textAlign: 'right',
            headerTextAlign: 'right',
            name: messages.taxRateColumnName,
            value: (li) => intl.formatNumber(li.taxRate, 'auto'),
            minWidth: 60,
        },
        {
            id: 'taxAmount',
            textAlign: 'right',
            headerTextAlign: 'right',
            name: messages.taxAmountColumnName,
            value: (li) => intl.formatNumber(li.taxAmount, 'auto'),
            minWidth: 100,
        },
        {
            id: 'grossAmount',
            textAlign: 'right',
            headerTextAlign: 'right',
            name: messages.grossAmountColumnName,
            value: (li) => intl.formatNumber(li.grossAmount, 'auto'),
            minWidth: 100,
        },
        {
            id: 'memo',
            name: messages.memoColumnName,
            value: (li) => li.memo,
            minWidth: 210,
        },
        {
            id: 'department',
            name: messages.departmentColumnName,
            value: (li) => li.department?.text,
            minWidth: 150,
        },
        {
            id: 'class',
            name: messages.classColumnName,
            value: (li) => li.class?.text,
            minWidth: 100,
        },
        {
            id: 'location',
            name: messages.locationColumnName,
            value: (li) => li.location?.text,
            minWidth: 100,
        },
        {
            id: 'customer',
            name: messages.customerColumnName,
            value: (li) => li.customer?.text,
            minWidth: 100,
        },
        {
            id: 'isBillable',
            name: messages.isBillableColumnName,
            value: (li) =>
                li.isBillable ? (
                    <Flex alignItems='center'>
                        <DoneIcon width={13} height={10} />
                    </Flex>
                ) : null,
            minWidth: 50,
        },
        ...customFieldsColumnDefinitions
    );

    if (sourceLine) {
        columnDefinitions.push(
            {
                id: 'remaining',
                textAlign: 'right',
                headerTextAlign: 'right',
                name: messages.remainingColumnName,
                value: (li) => ('remainingBalance' in li ? intl.formatNumber(li.remainingBalance, 'auto') : null),
                minWidth: 100,
            },
            {
                id: 'linked',
                name: messages.linkedColumnName,
                value: (li) => li.isMatched,
                cellComponent: (props) => {
                    const disabled = !('requestId' in props.item)
                        ? false
                        : isLineMatchingDisabled(props.item, lineType, sourceLine);

                    return (
                        <Table.Cell {...props}>
                            {props.item.isMatched ? <LinkIcon color={disabled ? 'midnight70' : 'green100'} /> : null}
                        </Table.Cell>
                    );
                },
                minWidth: 40,
                textAlign: 'right',
                spacing: '0 8',
            }
        );
    }

    return columnDefinitions.filter(({ id }) => {
        const setting = typeGuardHelpers.isKeyOfObject(id, fieldSettings) ? fieldSettings[id] : undefined;

        return isFieldVisible(setting);
    });
};
